import { useEffect, useState } from 'react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';

import { BackButton, StyledDialogTitle, StyledTableCell } from '../StyledComponents';
import PartDBService from '../../services/part-db-service';
import useErrorHandler from '../../services/error-handler';

//=================================================================================================
class ProjectStore {
    loading = false;
    projects = [];
    filter = {};

    constructor() {
        makeAutoObservable(this);
    }

    async load() {
        this.loading = true;
        try {
            const response = await PartDBService.projects(this.filter);
            this.projects = response.data;
        } catch (e) {
            throw e;
        } finally {
            this.loading = false;
        }
    }

    async setFilter(filter) {
        this.filter = filter;
        await this.load();
    }
}

const projectStore = new ProjectStore();

//=================================================================================================
class TableStore {
    loading = false;
    data = [];

    constructor() {
        makeAutoObservable(this);
    }

    async load() {
        this.loading = true;
        try {
            const response = await PartDBService.table();
            this.data = response.data;
        } catch (e) {
            throw e;
        } finally {
            this.loading = false;
        }
    }
}

const tableStore = new TableStore();

//=================================================================================================
const ProjectSelector = observer(({ project = null, onChange }) => {
    const errorHandler = useErrorHandler();

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        try {
            await projectStore.load();
        } catch (e) {
            errorHandler(e);
        }
    };
    const onSelectionChange = async (event, value) => {
        if (value) onChange(value);
    };

    const onInputChange = async (value) => {
        try {
            if (!value || value.length === 0) await projectStore.setFilter({});
            else await projectStore.setFilter({ name: value });
        } catch (e) {
            errorHandler(e);
        }
    };

    return (
        <Autocomplete
            fullWidth
            size="small"
            options={projectStore.projects}
            getOptionLabel={(i) => i.name}
            loading={projectStore.loading}
            value={project}
            onChange={onSelectionChange}
            onInputChange={(_, value) => onInputChange(value)}
            renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Проект part-db" />}
        />
    );
});

//=================================================================================================
const CreateDialog = ({ item, onClose }) => {
    const errorHandler = useErrorHandler();

    const [hw, setHw] = useState('');
    const [project, setProject] = useState(null);

    const onKeyDown = (e) => {
        if (e.key === 'Enter') onApplyButtonClick();
    };

    const onApplyButtonClick = async () => {
        try {
            if (!project) throw new Error('Выберите проект');
            for (let i of tableStore.data) {
                for (let p of i.projects) {
                    if (p.id === project.id) throw new Error('Проект part-db уже используется');
                }
            }
            const response = await PartDBService.addProject(item.type_id, project.id, hw);
            if (response.status === 200) onClose();
        } catch (e) {
            errorHandler(e);
        }
    };
    if (!item) return null;
    return (
        <Dialog open={item} onClose={onClose}>
            <StyledDialogTitle>Создать соответствие</StyledDialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 2 }}>
                    <ProjectSelector onChange={(p) => setProject(p)} />
                    <TextField
                        label="hw version"
                        variant="outlined"
                        size="small"
                        margin="normal"
                        onKeyDown={onKeyDown}
                        value={hw}
                        onChange={(e) => setHw(e.target.value)}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Tooltip title="Создать соответствие">
                    <Button onClick={onApplyButtonClick} color="primary" variant="contained">
                        Создать
                    </Button>
                </Tooltip>
                <Button onClick={onClose} variant="outlined">
                    Отмена
                </Button>
            </DialogActions>
        </Dialog>
    );
};

//=================================================================================================
//@warning здесь project имеет дополнительное свойство type_id !!!
const EditDialog = ({ project, onClose }) => {
    const errorHandler = useErrorHandler();

    const [hw, setHw] = useState('');

    useEffect(() => {
        if (project) setHw(project.hw_version);
    }, [project]);

    const onKeyDown = (e) => {
        if (e.key === 'Enter') onApplyButtonClick();
    };

    const onApplyButtonClick = async () => {
        try {
            const response = await PartDBService.editProject(project.type_id, project.id, hw);
            if (response.status === 200) onClose();
        } catch (e) {
            errorHandler(e);
        }
    };

    if (!project) return null;
    return (
        <Dialog open={project} onClose={onClose}>
            <StyledDialogTitle>Редактировать соответствие</StyledDialogTitle>
            <DialogContent>
                <TextField
                    label="hw version"
                    variant="outlined"
                    size="small"
                    margin="normal"
                    onKeyDown={onKeyDown}
                    value={hw}
                    onChange={(e) => setHw(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onApplyButtonClick} color="primary" variant="contained">
                    Сохранить
                </Button>
                <Button onClick={onClose} variant="outlined">
                    Отмена
                </Button>
            </DialogActions>
        </Dialog>
    );
};

//=================================================================================================
const PartDbMatching = () => {
    const errorHandler = useErrorHandler();

    const [create, setCreate] = useState(null);
    const [edit, setEdit] = useState(null);

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        try {
            await tableStore.load();
        } catch (e) {
            errorHandler(e);
        }
    };

    const onDeleteProject = async (project) => {
        try {
            await PartDBService.remove(project.type_id, project.id);
            init();
        } catch (e) {
            errorHandler(e);
        }
    };

    const renderPartDbChips = (item) => {
        const label = (project) => {
            let lbl = project.name;
            if (project.hw_version.length) lbl += ' hw:' + project.hw_version;
            return lbl;
        };
        const projects = item.projects.map((project) => (
            <Tooltip title="Редактировать" key={project.id}>
                <Chip
                    size="small"
                    label={label(project)}
                    color="primary"
                    onDelete={() => onDeleteProject({ ...project, type_id: item.type_id })}
                    onClick={() => setEdit({ ...project, type_id: item.type_id })}
                />
            </Tooltip>
        ));
        projects.push(
            <Tooltip title="Создать" key="create">
                <Chip size="small" label="+" onClick={() => setCreate(item)} />
            </Tooltip>
        );
        return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>{projects}</Box>;
    };

    const renderTableBody = () => {
        return tableStore.data.map((item) => (
            <TableRow hover key={item.type_id}>
                <TableCell>{item.name}</TableCell>
                <TableCell>{renderPartDbChips(item)}</TableCell>
            </TableRow>
        ));
    };

    return (
        <>
            <Stack spacing={2}>
                <Stack direction="row" alignItems="center">
                    <BackButton />
                    <Typography variant="h5" sx={{ marginTop: '0.2em', minHeight: '1.7em' }}>
                        Соответствие проектов part-db с portal
                    </Typography>
                </Stack>
                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell align="center">Наименование на portal</StyledTableCell>
                                <StyledTableCell align="center">Проекты в part-db</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>{renderTableBody()}</TableBody>
                    </Table>
                </TableContainer>
            </Stack>
            <CreateDialog
                item={create}
                onClose={() => {
                    setCreate(null);
                    init();
                }}
            />
            <EditDialog
                project={edit}
                onClose={() => {
                    setEdit(null);
                    init();
                }}
            />
        </>
    );
};

export default observer(PartDbMatching);
